import type { AIActionCard, AIActionStepResult } from '@/types/ai'

// ── Bhookly AI confirm cards: state and clock ───────────────────────────────
// The pure half of components/ai/ActionCard.tsx, kept here so it can be tested
// without a DOM. A card's status comes from the server; the widget only adds
// the server-clock offset it was received with (so a device whose clock is off
// still counts the undo minute down correctly) and its own request state.
//
// The undo window is enforced server-side (undo_until). The countdown only
// hides the button: it floors, so it never offers an undo the server is about
// to refuse.

/** What the staff member can do to a card from the widget. */
export type CardOp = 'confirm' | 'undo' | 'reject'

/** A card as the chat holds it: the server's copy plus what the widget knows. */
export interface ChatActionCard extends AIActionCard {
  /** Server clock minus device clock when this copy arrived (ms). */
  clockOffsetMs: number
  /** The request this widget has in flight for the card, if any. */
  busy?: CardOp
  /** A line under the card from a confirm / undo / dismiss that did not settle it. */
  problem?: string
}

/** What the card shows, from its status and the server clock. */
export type CardPhase =
  | 'pending'
  | 'expired'
  /** Confirmed or undoing on the server, with no result yet. */
  | 'working'
  | 'done'
  | 'partial'
  | 'failed'
  | 'undone'
  | 'undo_partial'
  | 'dismissed'

/** Statuses after which the change (or its undo) touched data: refresh its screens. */
export const CHANGED_STATUSES: ReadonlySet<string> = new Set(['executed', 'partially_executed', 'undone', 'undo_partial'])

/** Date.parse(server_now) - now; 0 when the server clock can't be read. */
export function clockOffsetFor(card: Pick<AIActionCard, 'server_now'>, receivedAt: number = Date.now()): number {
  const server = Date.parse(card.server_now)
  return Number.isFinite(server) ? server - receivedAt : 0
}

/** Wraps a card from the server, recording the clock offset at receipt. */
export function toChatCard(card: AIActionCard, receivedAt: number = Date.now()): ChatActionCard {
  return { ...card, clockOffsetMs: clockOffsetFor(card, receivedAt) }
}

function ranAndUndoable(card: AIActionCard): boolean {
  return card.status === 'executed' || card.status === 'partially_executed'
}

/** Milliseconds left until `iso` on the server's clock (NaN when unreadable). */
function msLeft(iso: string | null | undefined, clockOffsetMs: number, now: number): number {
  const at = iso ? Date.parse(iso) : NaN
  return at - (now + clockOffsetMs)
}

/** Whole seconds left to undo, by the server's clock; 0 when there is nothing to undo. */
export function remainingUndoSeconds(card: AIActionCard, clockOffsetMs: number, now: number = Date.now()): number {
  if (!ranAndUndoable(card) || !card.card?.undo_available) return 0
  const ms = msLeft(card.undo_until, clockOffsetMs, now)
  return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0
}

export function canUndo(card: AIActionCard, clockOffsetMs: number, now: number = Date.now()): boolean {
  return remainingUndoSeconds(card, clockOffsetMs, now) > 0
}

/** "0:59" */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

export function cardPhase(card: AIActionCard, clockOffsetMs = 0, now: number = Date.now()): CardPhase {
  switch (card.status) {
    case 'pending':
      // The server answers 'expired' once it notices; the clock gets there first.
      return msLeft(card.expires_at, clockOffsetMs, now) > 0 ? 'pending' : 'expired'
    case 'confirmed':
    case 'undoing':
      return 'working'
    case 'executed':
      return 'done'
    case 'partially_executed':
      return 'partial'
    case 'failed':
      return 'failed'
    case 'undone':
      return 'undone'
    case 'undo_partial':
      return 'undo_partial'
    case 'rejected':
      return 'dismissed'
    default:
      // 'expired', and any status this bundle doesn't know: nothing to tap.
      return 'expired'
  }
}

/**
 * How long until the card would look different (pending → expired, or the
 * countdown's next second), so the component re-renders exactly then; null
 * when nothing on the card depends on the clock.
 */
export function msUntilNextChange(card: AIActionCard, clockOffsetMs: number, now: number = Date.now()): number | null {
  const phase = cardPhase(card, clockOffsetMs, now)
  if (phase === 'pending') return msLeft(card.expires_at, clockOffsetMs, now) + 50
  if (canUndo(card, clockOffsetMs, now)) return (msLeft(card.undo_until, clockOffsetMs, now) % 1000) + 20
  return null
}

/** Steps an undo left as they were: changed since, blocked, or the revert failed. */
export function skippedOnUndo(card: AIActionCard): AIActionStepResult[] {
  return (card.results ?? []).filter((r) => r.undo === 'changed_since' || r.undo === 'blocked' || r.undo === 'failed')
}

/** Steps a confirm did not complete (failed, or never reached). */
export function unfinishedSteps(card: AIActionCard): AIActionStepResult[] {
  return (card.results ?? []).filter((r) => r.status !== 'ok')
}

/** Text for an error code the server stores on a card (or sends on a refusal). */
export function describeCardError(code: string): string | undefined {
  switch (code) {
    case 'ai_action_stale':
      return 'Something changed since this card was made — ask again for a fresh card.'
    case 'ai_action_tampered':
      return 'This change no longer matches what was shown — ask again.'
    case 'ai_action_outdated':
      return 'This card is out of date — ask again.'
    case 'ai_action_forbidden':
      return "You can't make this change."
    case 'interrupted':
      return 'The change was interrupted — check the screen before asking again.'
    default:
      return undefined
  }
}

/** The first thing that went wrong, readable. */
export function firstError(card: AIActionCard): string | undefined {
  if (card.error) return describeCardError(card.error) ?? card.error
  return (card.results ?? []).find((r) => r.error)?.error
}

export interface CardErrorOutcome {
  /** Card fields the refusal settles. */
  patch?: Partial<AIActionCard>
  /** A line to show under the card. */
  problem?: string
  /** The card's state on the server is unknown from here: read it again. */
  refresh?: boolean
}

/**
 * What a refused confirm / undo / dismiss means for the card. `message` is the
 * server's own sentence where it has one (for ai_action_stale it names what
 * changed).
 */
export function cardErrorOutcome(op: CardOp, code: string | undefined, message?: string): CardErrorOutcome {
  if (op === 'reject') {
    return { problem: code ? "Couldn't dismiss this card — try again." : "Couldn't reach Bhookly — check the connection." }
  }
  switch (code) {
    case 'ai_action_expired':
      return { patch: { status: 'expired' } }
    case 'ai_action_stale':
    case 'ai_action_tampered':
    case 'ai_action_outdated':
    case 'ai_action_forbidden':
      // The server marked the card failed; the failed state carries the reason.
      return { patch: { status: 'failed', error: message || describeCardError(code) } }
    case 'ai_undo_expired':
      return { patch: { undo_until: null }, problem: 'The minute to undo has passed — the change is saved.' }
    case 'ai_action_already_resolved':
    case 'ai_action_not_undoable':
      return { problem: message || 'This change was already handled.', refresh: true }
    case 'ai_writes_unavailable':
      return {
        problem:
          op === 'undo'
            ? "Changes can't be undone right now — try again in a moment."
            : "Changes can't be made right now — try again in a moment.",
      }
    case 'ai_action_not_found':
      return { problem: "That change wasn't found — ask again." }
    case undefined:
      // No response: offline, or a timeout after the server may have run it.
      return { problem: "Couldn't reach Bhookly — check the connection.", refresh: true }
    default:
      return { problem: message || 'Something went wrong — please try again.' }
  }
}
